'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchProvenanceFoldersPage,
  fetchProvenanceTreePage,
  fetchProvenanceTreeSearch,
  provenanceKeys,
  type ProvenanceFoldersFilters,
  type ProvenanceFoldersPage,
  type ProvenanceTreePage,
  type TagMatch,
  type TreeNodeResponse,
  type TreeView,
} from '@/shared/cors-provenance-api';
import { useProvenanceSession } from './useProvenanceSession';

const DEFAULT_PAGE_SIZE = 200;
const DEFAULT_SEARCH_LIMIT = 100;
const DEFAULT_FOLDERS_PAGE_SIZE = 200;

interface TreeArgs {
  path?: string;
  fileStatusId?: string;
  view?: TreeView;
  tags?: string[];
  tagMatch?: TagMatch;
  contentCategories?: string[];
  pageSize?: number;
  enabled?: boolean;
}

/**
 * TanStack ``useInfiniteQuery`` hook for ``GET /provenance-service/v1/tree``.
 *
 * Mirrors the legacy ``useProvenanceFiles`` shape: callers read the flattened
 * tree via ``data?.pages.flatMap(p => p.items)`` and trigger more loads with
 * ``fetchNextPage()`` / ``hasNextPage``.
 *
 * Disabled until both ``cellId`` and the real access token are available on
 * ``AuthContext`` (gated by ``useProvenanceSession``).
 */
export function useProvenanceTree(args: TreeArgs = {}) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const pageSize = args.pageSize ?? DEFAULT_PAGE_SIZE;
  const enabled = sessionEnabled && (args.enabled ?? true);

  const query = useInfiniteQuery<ProvenanceTreePage, Error>({
    queryKey: provenanceKeys.treeList(cellId, {
      path: args.path,
      fileStatusId: args.fileStatusId,
      view: args.view,
      tags: args.tags,
      tagMatch: args.tagMatch,
      contentCategories: args.contentCategories,
      pageSize,
    }),
    queryFn: ({ pageParam, signal }) =>
      fetchProvenanceTreePage({
        cellId: cellId as string,
        accessToken: accessToken as string,
        path: args.path,
        fileStatusId: args.fileStatusId,
        view: args.view,
        tags: args.tags,
        tagMatch: args.tagMatch,
        contentCategories: args.contentCategories,
        offset: typeof pageParam === 'number' ? pageParam : 0,
        limit: pageSize,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return Object.assign(query, { cellId });
}

interface TreeSearchArgs {
  q?: string;
  view?: TreeView;
  contentCategories?: string[];
  tags?: string[];
  tagMatch?: TagMatch;
  limit?: number;
  enabled?: boolean;
}

/**
 * TanStack ``useQuery`` hook for ``GET /provenance-service/v1/tree/search``.
 *
 * Search collapses the hierarchy into a flat ``TreeNodeResponse[]``;
 * directory matches and file/subdocument matches are intermixed and each
 * file/subdocument result carries ``source_file`` for breadcrumb rendering.
 */
export function useProvenanceTreeSearch(args: TreeSearchArgs = {}) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const limit = args.limit ?? DEFAULT_SEARCH_LIMIT;
  const hasSearchInput =
    (args.q !== undefined && args.q.trim() !== '') ||
    (args.contentCategories !== undefined && args.contentCategories.length > 0) ||
    (args.tags !== undefined && args.tags.length > 0);
  const enabled = sessionEnabled && hasSearchInput && (args.enabled ?? true);

  const query = useQuery<TreeNodeResponse[], Error>({
    queryKey: provenanceKeys.treeSearchFor(cellId, {
      q: args.q,
      view: args.view,
      contentCategories: args.contentCategories,
      tags: args.tags,
      tagMatch: args.tagMatch,
      limit,
    }),
    queryFn: ({ signal }) =>
      fetchProvenanceTreeSearch({
        cellId: cellId as string,
        accessToken: accessToken as string,
        q: args.q,
        view: args.view,
        contentCategories: args.contentCategories,
        tags: args.tags,
        tagMatch: args.tagMatch,
        limit,
        signal,
      }),
    enabled,
    staleTime: 60 * 1000,
  });

  return Object.assign(query, { cellId });
}

interface FoldersArgs {
  searchQuery?: string;
  tags?: string[];
  tagMatch?: TagMatch;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * TanStack ``useInfiniteQuery`` hook for ``GET /provenance-service/v1/folders``.
 *
 * Returns *every* directory ancestor (not just top-level entries) as a flat,
 * paginated list. Use for the EvidenceLocker's "Folders" filter mode when
 * the user wants to scope by folder without descending the tree.
 */
export function useProvenanceFolders(args: FoldersArgs = {}) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const pageSize = args.pageSize ?? DEFAULT_FOLDERS_PAGE_SIZE;
  const enabled = sessionEnabled && (args.enabled ?? true);

  const filters: ProvenanceFoldersFilters = {
    searchQuery: args.searchQuery,
    tags: args.tags,
    tagMatch: args.tagMatch,
  };

  const query = useInfiniteQuery<ProvenanceFoldersPage, Error>({
    queryKey: provenanceKeys.foldersList(cellId, filters, pageSize),
    queryFn: ({ pageParam, signal }) =>
      fetchProvenanceFoldersPage({
        cellId: cellId as string,
        accessToken: accessToken as string,
        offset: typeof pageParam === 'number' ? pageParam : 0,
        limit: pageSize,
        filters,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled,
    staleTime: 60 * 1000,
  });

  return Object.assign(query, { cellId });
}
